import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Eye, MoreHorizontal, Shield, Mail, Phone, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { systemUsers, permissionGroups } from '../data/mockData';
import { SystemUser } from '../types';
export function UserManagement() {
  const [users, setUsers] = useState<SystemUser[]>(systemUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  // Form State
  const [formData, setFormData] = useState<Partial<SystemUser>>({
    name: '',
    name_en: '',
    mobile: '',
    email: '',
    username: '',
    password: '',
    permission_group_id: '',
    status: 'active'
  });
  const filteredUsers = users.filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.name_en.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.username.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleCreateOpen = () => {
    setFormData({
      name: '',
      name_en: '',
      mobile: '',
      email: '',
      username: '',
      password: '',
      permission_group_id: permissionGroups[0]?.id || '',
      status: 'active'
    });
    setIsCreateModalOpen(true);
  };
  const handleEditOpen = (user: SystemUser) => {
    setSelectedUser(user);
    setFormData({
      ...user,
      password: ''
    }); // Don't show password
    setIsEditModalOpen(true);
  };
  const handleDetailsOpen = (user: SystemUser) => {
    setSelectedUser(user);
    setIsDetailsModalOpen(true);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(u => u.id !== id));
    }
  };
  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newUser: SystemUser = {
      ...(formData as SystemUser),
      id: `u${Date.now()}`,
      created_at: new Date().toISOString().split('T')[0]
    };
    setUsers([newUser, ...users]);
    setIsCreateModalOpen(false);
  };
  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const updatedUsers = users.map(u => u.id === selectedUser.id ? {
      ...u,
      ...formData
    } as SystemUser : u);
    setUsers(updatedUsers);
    setIsEditModalOpen(false);
  };
  const getGroupName = (id: string) => {
    const group = permissionGroups.find(g => g.id === id);
    return group ? group.name_en : 'Unknown';
  };
  return <div className="space-y-6" data-id="element-614">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-615">
        <div data-id="element-616">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-617">User Management</h1>
          <p className="text-slate-500" data-id="element-618">
            Manage system users and their access roles
          </p>
        </div>
        <Button onClick={handleCreateOpen} data-id="element-619">
          <Plus className="mr-2 h-4 w-4" data-id="element-620" /> Add New User
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col sm:flex-row gap-4" data-id="element-621">
        <div className="relative flex-1" data-id="element-622">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-623" />
          <input type="text" placeholder="Search by name, email, or username..." className="w-full h-10 pl-9 pr-4 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-624" />
        </div>
        <div className="w-full sm:w-48" data-id="element-625">
          <Select options={[{
          value: 'all',
          label: 'All Roles'
        }, ...permissionGroups.map(g => ({
          value: g.id,
          label: g.name_en
        }))]} data-id="element-626" />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-id="element-627">
        <div className="overflow-x-auto" data-id="element-628">
          <table className="w-full text-sm text-left" data-id="element-629">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-630">
              <tr data-id="element-631">
                <th className="px-6 py-3" data-id="element-632">Name</th>
                <th className="px-6 py-3" data-id="element-633">Contact Info</th>
                <th className="px-6 py-3" data-id="element-634">Username</th>
                <th className="px-6 py-3" data-id="element-635">Role</th>
                <th className="px-6 py-3" data-id="element-636">Status</th>
                <th className="px-6 py-3 text-right" data-id="element-637">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-638">
              {filteredUsers.length > 0 ? filteredUsers.map(user => <tr key={user.id} className="hover:bg-slate-50 transition-colors" data-id="element-639">
                    <td className="px-6 py-4" data-id="element-640">
                      <div className="font-medium text-slate-900" data-id="element-641">
                        {user.name_en}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-642">{user.name}</div>
                    </td>
                    <td className="px-6 py-4" data-id="element-643">
                      <div className="flex items-center text-slate-600 mb-1" data-id="element-644">
                        <Mail className="h-3 w-3 mr-1.5" data-id="element-645" /> {user.email}
                      </div>
                      <div className="flex items-center text-slate-600" data-id="element-646">
                        <Phone className="h-3 w-3 mr-1.5" data-id="element-647" /> {user.mobile}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-648">
                      {user.username}
                    </td>
                    <td className="px-6 py-4" data-id="element-649">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-650">
                        {getGroupName(user.permission_group_id)}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-651">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-652">
                        {user.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-653">
                      <div className="flex items-center justify-end space-x-2" data-id="element-654">
                        <button onClick={() => handleDetailsOpen(user)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="View Details" data-id="element-655">
                          <Eye size={16} data-id="element-656" />
                        </button>
                        <button onClick={() => handleEditOpen(user)} className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors" title="Edit User" data-id="element-657">
                          <Edit2 size={16} data-id="element-658" />
                        </button>
                        <button onClick={() => handleDelete(user.id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete User" data-id="element-659">
                          <Trash2 size={16} data-id="element-660" />
                        </button>
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-661">
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500" data-id="element-662">
                    No users found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center text-sm text-slate-500" data-id="element-663">
          <div data-id="element-664">
            Showing {filteredUsers.length} of {users.length} users
          </div>
          <div className="flex space-x-2" data-id="element-665">
            <button className="px-3 py-1 border border-slate-300 rounded hover:bg-white disabled:opacity-50" disabled data-id="element-666">
              Previous
            </button>
            <button className="px-3 py-1 border border-slate-300 rounded hover:bg-white disabled:opacity-50" disabled data-id="element-667">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Add New User" size="lg" data-id="element-668">
        <form onSubmit={handleCreateSubmit} className="space-y-4" data-id="element-669">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-670">
            <Input label="Name (English)" value={formData.name_en} onChange={e => setFormData({
            ...formData,
            name_en: e.target.value
          })} required data-id="element-671" />
            <Input label="Name (Bengali)" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} required data-id="element-672" />
            <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} required data-id="element-673" />
            <Input label="Mobile Number" value={formData.mobile} onChange={e => setFormData({
            ...formData,
            mobile: e.target.value
          })} required data-id="element-674" />
            <Input label="Username" value={formData.username} onChange={e => setFormData({
            ...formData,
            username: e.target.value
          })} required data-id="element-675" />
            <Input label="Password" type="password" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} required data-id="element-676" />
            <Select label="Permission Group" options={permissionGroups.map(g => ({
            value: g.id,
            label: g.name_en
          }))} value={formData.permission_group_id} onChange={e => setFormData({
            ...formData,
            permission_group_id: e.target.value
          })} data-id="element-677" />
            <Select label="Status" options={[{
            value: 'active',
            label: 'Active'
          }, {
            value: 'inactive',
            label: 'Inactive'
          }]} value={formData.status} onChange={e => setFormData({
            ...formData,
            status: e.target.value as 'active' | 'inactive'
          })} data-id="element-678" />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-4" data-id="element-679">
            <Button type="button" variant="secondary" onClick={() => setIsCreateModalOpen(false)} data-id="element-680">
              Cancel
            </Button>
            <Button type="submit" data-id="element-681">Create User</Button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit User" size="lg" data-id="element-682">
        <form onSubmit={handleEditSubmit} className="space-y-4" data-id="element-683">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-684">
            <Input label="Name (English)" value={formData.name_en} onChange={e => setFormData({
            ...formData,
            name_en: e.target.value
          })} required data-id="element-685" />
            <Input label="Name (Bengali)" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} required data-id="element-686" />
            <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} required data-id="element-687" />
            <Input label="Mobile Number" value={formData.mobile} onChange={e => setFormData({
            ...formData,
            mobile: e.target.value
          })} required data-id="element-688" />
            <Input label="Username" value={formData.username} onChange={e => setFormData({
            ...formData,
            username: e.target.value
          })} required data-id="element-689" />
            <Input label="Password (Leave blank to keep current)" type="password" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} data-id="element-690" />
            <Select label="Permission Group" options={permissionGroups.map(g => ({
            value: g.id,
            label: g.name_en
          }))} value={formData.permission_group_id} onChange={e => setFormData({
            ...formData,
            permission_group_id: e.target.value
          })} data-id="element-691" />
            <Select label="Status" options={[{
            value: 'active',
            label: 'Active'
          }, {
            value: 'inactive',
            label: 'Inactive'
          }]} value={formData.status} onChange={e => setFormData({
            ...formData,
            status: e.target.value as 'active' | 'inactive'
          })} data-id="element-692" />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-4" data-id="element-693">
            <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)} data-id="element-694">
              Cancel
            </Button>
            <Button type="submit" data-id="element-695">Save Changes</Button>
          </div>
        </form>
      </Modal>

      {/* User Details Modal */}
      <Modal isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} title="User Details" data-id="element-696">
        {selectedUser && <div className="space-y-6" data-id="element-697">
            <div className="flex items-center space-x-4 pb-6 border-b border-slate-100" data-id="element-698">
              <div className="h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-2xl font-bold" data-id="element-699">
                {selectedUser.name_en.charAt(0)}
              </div>
              <div data-id="element-700">
                <h3 className="text-xl font-bold text-slate-900" data-id="element-701">
                  {selectedUser.name_en}
                </h3>
                <p className="text-slate-500" data-id="element-702">{selectedUser.name}</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${selectedUser.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-703">
                  {selectedUser.status === 'active' ? 'Active Account' : 'Inactive Account'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-704">
              <div data-id="element-705">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-706">
                  Username
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-707">
                  {selectedUser.username}
                </p>
              </div>
              <div data-id="element-708">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-709">
                  Role
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-710">
                  {getGroupName(selectedUser.permission_group_id)}
                </p>
              </div>
              <div data-id="element-711">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-712">
                  Email
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-713">
                  {selectedUser.email}
                </p>
              </div>
              <div data-id="element-714">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-715">
                  Mobile
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-716">
                  {selectedUser.mobile}
                </p>
              </div>
              <div data-id="element-717">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-718">
                  Created At
                </label>
                <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-719">
                  {selectedUser.created_at}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-slate-100" data-id="element-720">
              <Button variant="secondary" onClick={() => setIsDetailsModalOpen(false)} data-id="element-721">
                Close
              </Button>
              <Button onClick={() => {
            setIsDetailsModalOpen(false);
            handleEditOpen(selectedUser);
          }} data-id="element-722">
                Edit User
              </Button>
            </div>
          </div>}
      </Modal>
    </div>;
}